'use client';

import { Badge } from '@ophelos/ui/components/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@ophelos/ui/components/card';
import { ArrowDownRight, ArrowUpRight, CheckCircle } from 'lucide-react';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { useTranslation } from '@/hooks/useTranslation';
import { fromMillicents } from '@/lib/currency';
import type { TransactionItem } from '@/types/core-api';
import { formatLedgerDate } from '../_shared/charges';
import { transactionTitle } from '../_shared/TransactionsList';
import { groupByPayment, paymentGroupDebtLabel } from './paymentGroups';

interface GroupedPaymentsListProps {
  transactions: TransactionItem[];
  /** From {@link debtNameByTransaction} — names the case each split landed on. */
  debtNames: Map<string, string>;
  /** A transaction carries no currency of its own, so the account's has to be handed down. */
  currency: string;
}

export function GroupedPaymentsList({ transactions, debtNames, currency }: GroupedPaymentsListProps) {
  const { t, currentLanguage } = useTranslation();
  const groups = groupByPayment(transactions);

  if (groups.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>{t('transaction.noPaymentHistory')}</p>
      </div>
    );
  }

  return (
    <div className="flex-1 stack min-h-0 max-w-xl mx-auto w-full">
      <Card variant="fullscreen" className="pb-2">
        <CardHeader>
          <CardTitle>{t('paymentHistory.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 px-2">
          {groups.map((group) => {
            const lead = group.splits[0];
            const isCredit = lead.direction === 'credit';
            const DirectionIcon = isCredit ? ArrowDownRight : ArrowUpRight;
            const [titleKey, titleValues] = transactionTitle(lead);
            // An unmapped method resolves to '' and drops out, rather than showing a raw value.
            const methodLabel = t(`paymentMethod.${lead.metadata?.payment_method}`, { defaultValue: '' });

            return (
              <div key={group.key} className="py-3 px-3 w-full">
                <div className="flex items-start justify-between gap-4 w-full">
                  <div className="flex gap-3 flex-1 min-w-0">
                    <div className={`size-8 rounded-full flex items-center justify-center shrink-0 ${isCredit ? 'highlight-green' : 'bg-soft'}`}>
                      <DirectionIcon className="size-4" />
                    </div>
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h5 className="font-medium text-sm whitespace-normal text-left">{t(titleKey, titleValues)}</h5>
                        <Badge variant="outline">
                          <CheckCircle className="size-3.5 text-green-600" />
                          {t('status.completed')}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
                        <span className="whitespace-nowrap">{formatLedgerDate(group.createdAt, currentLanguage)}</span>
                        {methodLabel && (
                          <>
                            <span>•</span>
                            <span className="whitespace-nowrap">{methodLabel}</span>
                          </>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground text-left">
                        {t('transaction.appliedTo')} <span className="font-medium">{paymentGroupDebtLabel(group, debtNames, t)}</span>
                      </div>
                    </div>
                  </div>
                  <div className={`inline-flex gap-2 items-center ${isCredit ? 'text-green-600' : ''}`}>
                    <span className="text-base">{isCredit ? '-' : ''}</span>
                    <CurrencyDisplay amount={fromMillicents(group.amountMillicents)} currency={currency} className="font-semibold text-base" />
                  </div>
                </div>

                {/* Outside the text column so each split's amount lands on the same edge as the total above. */}
                {group.splits.length > 1 && (
                  <div className="mt-2 ml-11 space-y-1">
                    {group.splits.map((split) => (
                      <div key={split.transaction_id} className="flex items-center justify-between gap-3 text-sm text-muted-foreground">
                        <span className="font-medium truncate">{debtNames.get(split.transaction_id) ?? t('transaction.payment')}</span>
                        <CurrencyDisplay amount={fromMillicents(split.amount_millicents)} currency={currency} className="shrink-0" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
